/**
 * Typed client for the host portal's Team screen
 * (workers/connect-api/src/handlers/team.ts).
 *
 * Cloudflare Access gates every one of these endpoints in production — the
 * browser's Access cookie is the credential and there is no client-side auth
 * code. In local dev there is no Access in front of `wrangler dev`, so the
 * calls carry the allow-listed `x-admin-email` bypass header, which
 * `import.meta.env.DEV` compiles out of production builds. Same pattern as
 * lib/api.ts and lib/registrations.ts.
 */
import { ApiError } from "./api";

export { ApiError };

export type Role = "admin" | "super_admin";

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  super_admin: "Super admin",
};

export interface TeamMember {
  email: string;
  role: Role;
  invited_by: string | null;
  invited_at: string;
  last_seen_at: string | null;
  created_at: string;
}

export interface Me {
  email: string;
  role: Role;
  is_super: boolean;
}

export interface TeamResult {
  members: TeamMember[];
  /** Live Cloudflare Access allow-list — everyone who can actually sign in. */
  access_emails: string[];
  /** False when the Worker has no Cloudflare API token (invites are disabled). */
  access_configured: boolean;
  /** Why the Access list is empty, when it is. */
  access_error?: string;
  /** True when the signed-in operator is a super admin. */
  can_manage: boolean;
  /** The signed-in operator's own email, lowercased. */
  me: string;
}

export interface InviteResult {
  member: TeamMember;
  emailed: boolean;
  email_error?: string;
}

/** Dev-only identity for the Worker's x-admin-email bypass (see lib/api.ts). */
const DEV_ADMIN_EMAIL =
  (import.meta.env.VITE_ADMIN_EMAIL as string | undefined) ?? "vc.le21@gmail.com";

function errorMessage(status: number, body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const rec = body as Record<string, unknown>;
    for (const key of ["error", "message"]) {
      if (typeof rec[key] === "string" && rec[key] !== "") return rec[key] as string;
    }
  }
  return `${fallback} (${status})`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (import.meta.env.DEV) headers["x-admin-email"] = DEV_ADMIN_EMAIL;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Network error — please check your connection and try again.");
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) throw new ApiError(res.status, errorMessage(res.status, json, `${method} ${path} failed`));
  return json as T;
}

/** GET /api/admin/me — who am I signed in as, and may I manage the team? */
export function fetchMe(): Promise<Me> {
  return request<Me>("GET", "/api/admin/me");
}

/** GET /api/admin/team — the recorded team plus the live Access allow-list. */
export function fetchTeam(): Promise<TeamResult> {
  return request<TeamResult>("GET", "/api/admin/team");
}

/** POST /api/admin/team — add to Access, record the role, email the invite. */
export function inviteMember(email: string, role: Role): Promise<InviteResult> {
  return request<InviteResult>("POST", "/api/admin/team", { email, role });
}

/** PATCH /api/admin/team/:email — change what someone may do. */
export function setMemberRole(email: string, role: Role): Promise<{ member: TeamMember }> {
  return request("PATCH", `/api/admin/team/${encodeURIComponent(email)}`, { role });
}

/** DELETE /api/admin/team/:email — revoke access immediately. */
export function removeMember(email: string): Promise<{ ok: true; email: string }> {
  return request("DELETE", `/api/admin/team/${encodeURIComponent(email)}`);
}
